import React, { useEffect, useRef, useState } from "react";

// COMPONENT IMPORTS
import OutsideClickHandler from "components/ui-component/OutsideClickHandler";
import { MoreVerticalIcon } from "icons/payments/MoreVerticalIcon";
import { useTid } from "utils/testid";

export interface PaymentMethodAction {
  key: string;
  dividerBefore?: boolean;
  // A plain action renders its own row…
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  isDestructive?: boolean;
  // …or a self-contained control supplies the row itself, for actions that
  // carry their own state (bank reconnect has to own the Plaid link handshake).
  node?: React.ReactNode;
}

export const actionMenuItemClass =
  "w-full flex items-center gap-[10px] px-3 py-[10px] rounded-lg text-sm leading-[normal] text-left hover:bg-bgSecondary";

interface Props {
  actions: PaymentMethodAction[];
  triggerLabel: string;
  testId: string;
  // Payment cards are dark blue and need a light trigger; bank and wallet
  // tiles are near-white and need the same white circle the hover icons use.
  tone?: "onDark" | "onLight";
  // Each tile fades in, which gives it its own stacking context, so an open
  // menu would be painted under the following tile. The tile lifts its own
  // z-index while the menu is open.
  onOpenChange?: (isOpen: boolean) => void;
}

/* --------------------- PAYMENT METHOD ACTIONS MENU (MOBILE) --------------------- */

// Below `lg` there is no hover, so the delete / edit / set-as-default icons that
// the desktop card reveals on hover are unreachable. This replaces them with a
// tappable menu on small screens; desktop keeps the hover icons.
const PaymentMethodActionsMenu: React.FC<Props> = ({
  actions,
  triggerLabel,
  testId,
  tone = "onDark",
  onOpenChange,
}) => {
  const tid = useTid();
  const [isOpen, setIsOpenState] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const setIsOpen = (next: boolean) => {
    setIsOpenState(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // A tile near the bottom of the screen would otherwise open its menu below
    // the fold. Scroll by the least amount that brings the whole menu into
    // view, so nothing has to be scrolled to by hand.
    const frame = requestAnimationFrame(() => {
      menuRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  if (actions.length === 0) {
    return null;
  }

  return (
    <OutsideClickHandler
      onOutsideClick={() => setIsOpen(false)}
      // 22px from the right edge, 24px from the bottom, per the Figma card.
      className="absolute bottom-6 right-[22px] z-[3] xl:hidden"
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={triggerLabel}
        data-testid={tid(`${testId}-actions-button`)}
        onClick={() => setIsOpen(!isOpen)}
        // At rest the dots sit straight on the tile; the circle is the open
        // state, which is what the Figma frame shows. The 32px box is there
        // either way so the tap target stays comfortable on touch screens.
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isOpen ? (tone === "onDark" ? "bg-white/15" : "bg-bgPrimary") : ""
        }`}
      >
        <MoreVerticalIcon
          fillColor={
            tone === "onDark" ? "white" : "var(--color-icon-obscure)"
          }
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          data-testid={tid(`${testId}-actions-menu`)}
          // scroll-mb-4 keeps a little breathing room below the menu once it
          // has been scrolled into view.
          className="absolute right-0 top-[calc(100%+8px)] w-[180px] p-2 flex flex-col gap-1 bg-bgPrimary border border-strokeMenu rounded-[14px] shadow-[0_8px_10px_rgba(0,0,0,0.13)] scroll-mb-4"
        >
          {actions.map((action) => (
            <React.Fragment key={action.key}>
              {action.dividerBefore && (
                <div className="h-px w-full bg-strokeMenu" />
              )}
              {action.node ? (
                <div onClick={() => setIsOpen(false)}>{action.node}</div>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  data-testid={tid(`${testId}-actions-menu-${action.key}`)}
                  onClick={() => {
                    setIsOpen(false);
                    action.onClick?.();
                  }}
                  className={`${actionMenuItemClass} ${
                    action.isDestructive ? "text-red2" : "text-txtPrimary"
                  }`}
                >
                  <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                    {action.icon}
                  </span>
                  {action.label}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </OutsideClickHandler>
  );
};

export default PaymentMethodActionsMenu;
